import { EventBadge } from '../ui/EventBadge'
import { EVENT_TYPES, EVENT_TYPE_IMAGES } from '../ui/eventTypes'
import type { EventType } from '@fellowship/shared'

function fmt(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

interface Props {
  title: string
  description: string
  eventType: EventType
  startsAt: string
  endsAt: string
  churchName: string
  locationName?: string | null
  addressText: string
  imageUrl: string | null
  onClose: () => void
}

export function EventPreviewModal({
  title, description, eventType, startsAt, endsAt,
  churchName, locationName, addressText, imageUrl, onClose,
}: Props) {
  const t = EVENT_TYPES[eventType as keyof typeof EVENT_TYPES] ?? EVENT_TYPES.potluck
  const coverSrc = imageUrl ?? EVENT_TYPE_IMAGES[eventType] ?? EVENT_TYPE_IMAGES.potluck

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          width: '100%', maxWidth: 540,
          background: 'var(--surface)',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Cover image */}
        <div style={{ height: 180, overflow: 'hidden', position: 'relative' }}>
          <img
            src={coverSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div
            style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(to bottom, transparent 40%, ${t.color}88)`,
            }}
          />
          <div style={{ position: 'absolute', top: 14, left: 14 }}>
            <EventBadge type={eventType} size="md" />
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 12, right: 12,
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(0,0,0,0.4)', border: 'none', cursor: 'pointer',
              color: '#fff', fontSize: 18, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px 28px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.color, marginBottom: 6 }}>
            Preview — not published
          </div>

          <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--ink)', margin: '0 0 12px', lineHeight: 1.15, fontFamily: 'Fraunces, serif' }}>
            {title || 'Untitled event'}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, fontSize: 13.5, color: 'var(--ink-2)' }}>
            {startsAt && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ flexShrink: 0 }}>📅</span>
                <span>
                  {fmt(startsAt)}
                  {endsAt && ` – ${fmt(endsAt)}`}
                </span>
              </div>
            )}
            {(locationName || addressText) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ flexShrink: 0 }}>📍</span>
                {locationName ? (
                  <span>
                    <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{locationName}</span>
                    {addressText && addressText !== locationName && (
                      <span style={{ display: 'block', color: 'var(--muted)' }}>{addressText}</span>
                    )}
                  </span>
                ) : (
                  <span>{addressText}</span>
                )}
              </div>
            )}
            {churchName && (
              <div style={{ display: 'flex', gap: 8 }}>
                <span>⛪</span>
                <span>{churchName}</span>
              </div>
            )}
          </div>

          {description && (
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
              {description.slice(0, 300)}{description.length > 300 ? '…' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
